"use client"

import * as React from 'react'
import { useRouter } from 'next/navigation'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useT } from '@open-mercato/shared/lib/i18n/context'
import { useOrganizationScopeVersion } from '@open-mercato/shared/lib/frontend/useOrganizationScope'
import { Page, PageBody } from '@open-mercato/ui/backend/Page'
import { LoadingMessage, ErrorMessage } from '@open-mercato/ui/backend/detail'
import { FormHeader } from '@open-mercato/ui/backend/forms'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@open-mercato/ui/primitives/tabs'
import { Badge } from '@open-mercato/ui/primitives/badge'
import { flash } from '@open-mercato/ui/backend/FlashMessages'
import { useConfirmDialog } from '@open-mercato/ui/backend/confirm-dialog'
import { useGuardedMutation } from '@open-mercato/ui/backend/injection/useGuardedMutation'
import { readApiResultOrThrow } from '@open-mercato/ui/backend/utils/apiCall'
import { deleteCrud, updateCrud } from '@open-mercato/ui/backend/utils/crud'
import { FleetVehicleDialog, toFleetVehicleFormValues } from '../../../lib/fleetForm'
import {
  type FleetVehicleDetail,
  formatVehicleTypeLabel,
} from '../../../lib/fleetTable'
import {
  FleetAssignmentHistoryTab,
  FleetDocumentsTab,
  FleetMaintenanceTab,
  FleetOverviewTab,
} from '../../../lib/detailSections'

async function fetchFleetVehicleDetail(id: string): Promise<FleetVehicleDetail> {
  return readApiResultOrThrow<FleetVehicleDetail>(`/api/road/vehicles/${id}`, undefined, {
    errorMessage: 'Failed to fetch vehicle detail.',
  })
}

export default function FleetVehicleDetailPage({ params }: { params: { id: string } }) {
  const t = useT()
  const router = useRouter()
  const scopeVersion = useOrganizationScopeVersion()
  const queryClient = useQueryClient()
  const { confirm, ConfirmDialogElement } = useConfirmDialog()
  const { runMutation } = useGuardedMutation({ contextId: `fleet-detail-${params.id}` })
  const [activeTab, setActiveTab] = React.useState('overview')
  const [editOpen, setEditOpen] = React.useState(false)
  const [editPending, setEditPending] = React.useState(false)
  const [deletePending, setDeletePending] = React.useState(false)

  const vehicleQuery = useQuery({
    queryKey: ['fleet', 'vehicle', params.id, scopeVersion],
    queryFn: () => fetchFleetVehicleDetail(params.id),
  })

  const refreshVehicle = React.useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['fleet', 'vehicle', params.id] })
    await queryClient.invalidateQueries({ queryKey: ['fleet', 'vehicles'] })
  }, [params.id, queryClient])

  const vehicle = vehicleQuery.data ?? null

  const handleDelete = React.useCallback(async () => {
    if (!vehicle || deletePending) return
    const approved = await confirm({
      title: t('fleet.confirm.deleteTitle', 'Delete vehicle?'),
      text: t(
        'fleet.confirm.deleteMessage',
        'This will archive the vehicle and keep audit history intact.',
      ),
      confirmText: t('fleet.actions.delete', 'Delete'),
      cancelText: t('common.cancel', 'Cancel'),
      variant: 'destructive',
    })
    if (!approved) return
    setDeletePending(true)
    try {
      await runMutation({
        context: { kind: 'road.vehicle', record: { id: vehicle.id } },
        mutationPayload: { id: vehicle.id },
        operation: async () => {
          await deleteCrud(`road/vehicles/${vehicle.id}`, {
            errorMessage: t('fleet.messages.deleteError', 'Failed to delete vehicle.'),
          })
        },
      })
      flash(t('fleet.messages.deleteSuccess', 'Vehicle deleted.'), 'success')
      await refreshVehicle()
      router.push('/backend/fleet')
    } catch {
      flash(t('fleet.messages.deleteError', 'Failed to delete vehicle.'), 'error')
    } finally {
      setDeletePending(false)
    }
  }, [confirm, deletePending, refreshVehicle, router, runMutation, t, vehicle])

  if (vehicleQuery.isLoading && !vehicle) {
    return (
      <Page>
        <PageBody>
          <LoadingMessage label={t('fleet.messages.loadingDetail', 'Loading vehicle detail…')} />
        </PageBody>
      </Page>
    )
  }

  if (vehicleQuery.isError || !vehicle) {
    return (
      <Page>
        <PageBody>
          <ErrorMessage
            label={t('fleet.messages.loadErrorTitle', 'Could not load fleet')}
            description={t('fleet.messages.loadDetailError', 'The vehicle could not be loaded.')}
          />
        </PageBody>
      </Page>
    )
  }

  return (
    <Page>
      <FormHeader
        mode="detail"
        backHref="/backend/fleet"
        entityTypeLabel={t('fleet.detail.entityType', 'Vehicle')}
        title={vehicle.registrationPlate}
        subtitle={`${formatVehicleTypeLabel(t, vehicle.type)}${vehicle.brand ? ` • ${vehicle.brand}` : ''}${vehicle.model ? ` ${vehicle.model}` : ''}`}
        statusBadge={(
          <Badge variant={vehicle.isActive ? 'default' : 'muted'}>
            {vehicle.isActive ? t('fleet.detail.status.active', 'Active') : t('fleet.detail.status.inactive', 'Inactive')}
          </Badge>
        )}
        menuActions={[
          {
            id: 'edit',
            label: t('fleet.actions.editDetails', 'Edit details'),
            onSelect: () => setEditOpen(true),
          },
        ]}
        onDelete={() => {
          void handleDelete()
        }}
        isDeleting={deletePending}
        deleteLabel={t('fleet.actions.delete', 'Delete')}
      />

      <PageBody>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="overview">{t('fleet.detail.tabs.overview', 'Overview')}</TabsTrigger>
            <TabsTrigger value="documents">{t('fleet.detail.tabs.documents', 'Documents')}</TabsTrigger>
            <TabsTrigger value="maintenance">{t('fleet.detail.tabs.maintenance', 'Maintenance')}</TabsTrigger>
            <TabsTrigger value="assignmentHistory">{t('fleet.detail.tabs.assignmentHistory', 'Assignment history')}</TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            <FleetOverviewTab vehicle={vehicle} t={t} />
          </TabsContent>
          <TabsContent value="documents">
            <FleetDocumentsTab vehicle={vehicle} t={t} />
          </TabsContent>
          <TabsContent value="maintenance">
            <FleetMaintenanceTab t={t} />
          </TabsContent>
          <TabsContent value="assignmentHistory">
            <FleetAssignmentHistoryTab t={t} />
          </TabsContent>
        </Tabs>
      </PageBody>

      <FleetVehicleDialog
        open={editOpen}
        onOpenChange={(open) => {
          if (!editPending) setEditOpen(open)
        }}
        mode="edit"
        initialValues={toFleetVehicleFormValues(vehicle)}
        loading={editPending}
        onSubmit={async (payload) => {
          setEditPending(true)
          try {
            await updateCrud(`road/vehicles/${vehicle.id}`, payload, {
              errorMessage: t('fleet.messages.updateError', 'Failed to update vehicle.'),
            })
            flash(t('fleet.messages.updateSuccess', 'Vehicle updated.'), 'success')
            setEditOpen(false)
            await refreshVehicle()
          } finally {
            setEditPending(false)
          }
        }}
      />
      {ConfirmDialogElement}
    </Page>
  )
}
